/**
 * Connect Four search for DROP-9000.
 *
 * Negamax with alpha-beta over a mutable board, iterative deepening until a time budget (or a
 * fixed depth in tests), a Zobrist-keyed transposition table, centre-first move ordering plus
 * killer moves, and immediate win / must-block shortcuts. Scores are `WIN_SCORE - ply`, so it
 * wins as fast and loses as slowly as it can; with few cells left the search is exact.
 *
 * Server-only at runtime: the web never imports this file.
 */
import { seededRandom } from '../random'
import { C4_CELLS, C4_COLS, C4_ROWS, type C4State } from './engine'

export const WIN_SCORE = 100_000
/** Anything this far from zero is a forced result within the searched horizon. */
const FORCED = WIN_SCORE - 1_000
const INF = WIN_SCORE * 2

/** Columns from the middle out: the strongest default ordering in Connect Four. */
const ORDER: readonly number[] = [3, 2, 4, 1, 5, 0, 6]

const EMPTY = 0
type Piece = 1 | 2 // 1 = X (opens), 2 = O

// ---- the 69 four-cell windows -------------------------------------------------------------

const WINDOWS: number[][] = []
for (let r = 0; r < C4_ROWS; r++) {
  for (let c = 0; c < C4_COLS; c++) {
    const i = r * C4_COLS + c
    if (c + 3 < C4_COLS) WINDOWS.push([i, i + 1, i + 2, i + 3])
    if (r + 3 < C4_ROWS) WINDOWS.push([i, i + C4_COLS, i + 2 * C4_COLS, i + 3 * C4_COLS])
    if (c + 3 < C4_COLS && r + 3 < C4_ROWS)
      WINDOWS.push([i, i + C4_COLS + 1, i + 2 * (C4_COLS + 1), i + 3 * (C4_COLS + 1)])
    if (c - 3 >= 0 && r + 3 < C4_ROWS)
      WINDOWS.push([i, i + C4_COLS - 1, i + 2 * (C4_COLS - 1), i + 3 * (C4_COLS - 1)])
  }
}
const WINDOW_FLAT = Int8Array.from(WINDOWS.flat())
const WINDOW_COUNT = WINDOWS.length

// ---- Zobrist keys and the transposition table --------------------------------------------

const zrand = seededRandom(0xc4c4c4)
const zkey = () => Math.floor(zrand() * 0x100000000) | 0
const Z_LO = Int32Array.from({ length: C4_CELLS * 3 }, zkey)
const Z_HI = Int32Array.from({ length: C4_CELLS * 3 }, zkey)

const TT_BITS = 20
const TT_SIZE = 1 << TT_BITS
const TT_MASK = TT_SIZE - 1
const TT_EMPTY = 0
const TT_EXACT = 1
const TT_LOWER = 2
const TT_UPPER = 3
const tKeyLo = new Int32Array(TT_SIZE)
const tKeyHi = new Int32Array(TT_SIZE)
const tDepth = new Int8Array(TT_SIZE)
const tFlag = new Int8Array(TT_SIZE)
const tValue = new Int32Array(TT_SIZE)
const tMove = new Int8Array(TT_SIZE)

export interface SearchOptions {
  /** Wall-clock budget; the deepest fully completed iteration wins. */
  timeMs?: number
  /** Hard depth cap in plies (tests use this for determinism). */
  maxDepth?: number
  /**
   * Source in [0,1) that shuffles the root move order, so equally good moves vary from game to
   * game. Omit for a fixed centre-first order (deterministic).
   */
  random?: () => number
}

export interface SearchResult {
  column: number
  /** From the side to move's point of view. */
  score: number
  /** Deepest completed iteration. */
  depth: number
  nodes: number
  forced: 'win' | 'loss' | null
}

class Search {
  readonly cells = new Int8Array(C4_CELLS)
  readonly heights = new Int8Array(C4_COLS)
  private keyLo = 0
  private keyHi = 0
  nodes = 0
  aborted = false
  private readonly killers = new Int8Array(C4_CELLS * 2).fill(-1)
  private readonly deadline: number

  constructor(state: C4State, deadline: number) {
    this.deadline = deadline
    for (let i = 0; i < C4_CELLS; i++) {
      const m = state.board[i]
      if (m) this.place(i, m === 'X' ? 1 : 2)
    }
    for (let c = 0; c < C4_COLS; c++) this.heights[c] = state.heights[c]!
  }

  place(idx: number, p: Piece) {
    this.cells[idx] = p
    this.keyLo ^= Z_LO[idx * 3 + p]!
    this.keyHi ^= Z_HI[idx * 3 + p]!
  }

  lift(idx: number, p: Piece) {
    this.cells[idx] = EMPTY
    this.keyLo ^= Z_LO[idx * 3 + p]!
    this.keyHi ^= Z_HI[idx * 3 + p]!
  }

  /** Does dropping `p` in `col` complete a line? (Assumes the column is not full.) */
  wins(col: number, p: Piece): boolean {
    const row = this.heights[col]!
    const cells = this.cells
    // vertical
    if (row >= 3) {
      let n = 0
      for (let r = row - 1; r >= 0 && cells[r * C4_COLS + col] === p; r--) n++
      if (n >= 3) return true
    }
    // horizontal and both diagonals
    for (const [dr, dc] of DIRS) {
      let n = 0
      let r = row + dr
      let c = col + dc
      while (r >= 0 && r < C4_ROWS && c >= 0 && c < C4_COLS && cells[r * C4_COLS + c] === p) {
        n++
        r += dr
        c += dc
      }
      r = row - dr
      c = col - dc
      while (r >= 0 && r < C4_ROWS && c >= 0 && c < C4_COLS && cells[r * C4_COLS + c] === p) {
        n++
        r -= dr
        c -= dc
      }
      if (n >= 3) return true
    }
    return false
  }

  /** Static evaluation from `p`'s point of view: threats weighted by parity, centre control. */
  evaluate(p: Piece): number {
    const q: Piece = p === 1 ? 2 : 1
    const cells = this.cells
    const heights = this.heights
    let score = 0
    for (let w = 0; w < WINDOW_COUNT; w++) {
      const base = w * 4
      let mine = 0
      let theirs = 0
      let empty = -1
      for (let k = 0; k < 4; k++) {
        const idx = WINDOW_FLAT[base + k]!
        const v = cells[idx]
        if (v === p) mine++
        else if (v === q) theirs++
        else empty = idx
      }
      if (mine > 0 && theirs > 0) continue
      if (mine === 3) score += threatValue(empty, heights, p)
      else if (theirs === 3) score -= threatValue(empty, heights, q)
      else if (mine === 2) score += 6
      else if (theirs === 2) score -= 6
    }
    for (let r = 0; r < C4_ROWS; r++) {
      const mid = cells[r * C4_COLS + 3]
      if (mid === p) score += 3
      else if (mid === q) score -= 3
      const l = cells[r * C4_COLS + 2]
      const rr = cells[r * C4_COLS + 4]
      if (l === p) score += 1
      else if (l === q) score -= 1
      if (rr === p) score += 1
      else if (rr === q) score -= 1
    }
    return score
  }

  negamax(depth: number, alpha: number, beta: number, ply: number, p: Piece): number {
    this.nodes++
    if ((this.nodes & 4095) === 0 && Date.now() > this.deadline) {
      this.aborted = true
      return 0
    }
    const q: Piece = p === 1 ? 2 : 1
    const heights = this.heights

    // Win now if we can; block if we must (two threats = lost).
    let mustBlock = -1
    let threats = 0
    for (let k = 0; k < C4_COLS; k++) {
      const col = ORDER[k]!
      if (heights[col]! >= C4_ROWS) continue
      if (this.wins(col, p)) return WIN_SCORE - (ply + 1)
      if (this.wins(col, q)) {
        threats++
        mustBlock = col
      }
    }
    if (threats > 1) return -(WIN_SCORE - (ply + 2))
    if (depth <= 0) return this.evaluate(p)

    const slot = (this.keyLo ^ (this.keyHi * 0x9e3779b1)) & TT_MASK
    let ttMove = -1
    if (tFlag[slot] !== TT_EMPTY && tKeyLo[slot] === this.keyLo && tKeyHi[slot] === this.keyHi) {
      ttMove = tMove[slot]!
      if (tDepth[slot]! >= depth) {
        const v = tValue[slot]!
        const f = tFlag[slot]
        if (f === TT_EXACT) return v
        if (f === TT_LOWER && v >= beta) return v
        if (f === TT_UPPER && v <= alpha) return v
      }
    }

    const alphaOrig = alpha
    let best = -INF
    let bestMove = -1
    const k1 = this.killers[ply * 2]!
    const k2 = this.killers[ply * 2 + 1]!
    // Order: forced block, TT move, killers, then centre-out.
    for (let k = -3; k < C4_COLS; k++) {
      let col: number
      if (mustBlock >= 0) {
        if (k !== -3) break
        col = mustBlock
      } else if (k === -3) col = ttMove
      else if (k === -2) col = k1
      else if (k === -1) col = k2
      else {
        col = ORDER[k]!
        if (col === ttMove || col === k1 || col === k2) continue
      }
      if (col < 0 || heights[col]! >= C4_ROWS) continue

      const row = heights[col]!
      const idx = row * C4_COLS + col
      this.place(idx, p)
      heights[col] = row + 1
      let score: number
      if (row + 1 === C4_ROWS && this.full()) score = 0
      else score = -this.negamax(depth - 1, -beta, -alpha, ply + 1, q)
      heights[col] = row
      this.lift(idx, p)
      if (this.aborted) return 0

      if (score > best) {
        best = score
        bestMove = col
      }
      if (score > alpha) alpha = score
      if (alpha >= beta) {
        if (col !== k1) {
          this.killers[ply * 2 + 1] = k1
          this.killers[ply * 2] = col
        }
        break
      }
    }

    tKeyLo[slot] = this.keyLo
    tKeyHi[slot] = this.keyHi
    tDepth[slot] = depth
    tValue[slot] = best
    tMove[slot] = bestMove
    tFlag[slot] = best <= alphaOrig ? TT_UPPER : best >= beta ? TT_LOWER : TT_EXACT
    return best
  }

  private full(): boolean {
    for (let c = 0; c < C4_COLS; c++) if (this.heights[c]! < C4_ROWS) return false
    return true
  }
}

const DIRS: readonly [number, number][] = [
  [0, 1],
  [1, 1],
  [1, -1],
]

/**
 * A three-with-a-gap is worth more when the gap can't be filled yet and sits on the row parity
 * that favours its owner (the opener wants odd rows, counted from 1; the second player even).
 */
function threatValue(empty: number, heights: Int8Array, owner: Piece): number {
  const row = (empty / C4_COLS) | 0
  const col = empty % C4_COLS
  if (heights[col] === row) return 60 // playable right now: the search will see it
  const oddRow = row % 2 === 0 // 0-based even = 1-based odd
  return (owner === 1) === oddRow ? 45 : 18
}

/** Best column for the side to move. Throws on a finished game or a full board. */
export function searchC4(state: C4State, opts: SearchOptions = {}): SearchResult {
  if (state.status !== 'playing') throw new Error('c4 solver: game is over')
  const legal = ORDER.filter((c) => state.heights[c]! < C4_ROWS)
  if (legal.length === 0) throw new Error('c4 solver: no legal move')
  // Variety comes from the order moves are tried, never from picking among "equal" scores: with
  // alpha-beta a later move's score is only an upper bound, so it can look equal while being worse.
  const order = opts.random ? shuffled(legal, opts.random) : legal
  // The one-move opening book: the centre is the only first move that keeps the opener's win.
  if (state.moveCount === 0) return { column: 3, score: 0, depth: 0, nodes: 0, forced: null }
  const p: Piece = state.turn === 'X' ? 1 : 2
  const deadline = opts.timeMs === undefined ? Number.POSITIVE_INFINITY : Date.now() + opts.timeMs
  const cap = Math.min(opts.maxDepth ?? C4_CELLS, C4_CELLS - state.moveCount)
  tFlag.fill(TT_EMPTY)
  const s = new Search(state, deadline)

  let result: SearchResult = {
    column: legal[0]!,
    score: 0,
    depth: 0,
    nodes: 0,
    forced: null,
  }
  for (let depth = 1; depth <= cap; depth++) {
    let alpha = -INF
    let bestCol = -1
    for (const col of order) {
      const score = s.wins(col, p) ? WIN_SCORE - 1 : childScore(s, col, p, depth, alpha)
      if (s.aborted) break
      // Only a move that raises alpha has an exact score; the first such move keeps ties.
      if (score > alpha) {
        alpha = score
        bestCol = col
      }
    }
    if (s.aborted || bestCol < 0) break
    const forced = alpha >= FORCED ? 'win' : alpha <= -FORCED ? 'loss' : null
    // Leaves after the opponent's reply (even depth) judge a position honestly; a leaf right
    // after our own move over-rates the threats we just made. Odd depths are kept only when they
    // settle the game or are the last plies of it.
    if (depth % 2 === 0 || forced || depth === cap) {
      result = { column: bestCol, score: alpha, depth, nodes: s.nodes, forced }
    }
    if (forced) break
  }
  result.nodes = s.nodes
  return result
}

function shuffled(cols: readonly number[], random: () => number): number[] {
  const out = [...cols]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    const t = out[i]!
    out[i] = out[j]!
    out[j] = t
  }
  return out
}

function childScore(s: Search, col: number, p: Piece, depth: number, alpha: number): number {
  const row = s.heights[col]!
  const idx = row * C4_COLS + col
  const q: Piece = p === 1 ? 2 : 1
  s.heights[col] = row + 1
  s.place(idx, p)
  let score: number
  if (row + 1 === C4_ROWS && allFull(s.heights)) score = 0
  else score = -s.negamax(depth - 1, -INF, -alpha, 1, q)
  s.lift(idx, p)
  s.heights[col] = row
  return score
}

function allFull(heights: Int8Array): boolean {
  for (let c = 0; c < C4_COLS; c++) if (heights[c]! < C4_ROWS) return false
  return true
}

/** How the side to move stands within `maxDepth` plies: used to spot the human's losing move. */
export function c4Outlook(state: C4State, maxDepth: number): 'win' | 'loss' | 'unclear' {
  if (state.status !== 'playing') return 'unclear'
  return searchC4(state, { maxDepth }).forced ?? 'unclear'
}
